import { Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useFormikContext } from 'formik';
import IOSSwitch from '../../global/IOSSwitch';
import TextInputControlled from '../../form/base/TextInputControlled';
import { UpsertBusinessServiceInitValues } from '../../../types/businesses';
import { LoadedUser } from '../../../interfaces';
import useUser from '../../../hooks/global/useUser';

const NON_COMMISSION_TOOLTIP =
  'Only available for providers whose payroll is set up as commission-based.';

function ServiceCommissionsSection() {
  const { user } = useUser<LoadedUser>();
  const employees = user?.business?.employees ?? [];
  const { values, setFieldValue, isSubmitting } =
    useFormikContext<UpsertBusinessServiceInitValues>();

  const selectedEmployees = employees.filter(
    e => values.employeeIds?.includes(e.employeeId),
  );

  if (!selectedEmployees.length) return null;

  const getEtbRate = (
    employee: LoadedUser['business']['employees'][number],
  ) => {
    if (employee.payrollType === 'COMMISSION') {
      return (employee.paymentToEmployeePercent ?? 0) * 100;
    }
    if (employee.payrollType === 'RENT') return 100;
    if (employee.payrollType === 'WAGE') return 0;
    // OTHER
    return employee.payeeForOtherPayroll === 'EMPLOYEE' ? 100 : 0;
  };

  const getCommission = (employeeId: string) =>
    values.employeeCommissions?.find(c => c.employeeId === employeeId);

  const setEnabled = (employeeId: string, enabled: boolean) => {
    const existing = values.employeeCommissions ?? [];
    const current = existing.find(c => c.employeeId === employeeId);
    const etbRate = getEtbRate(
      employees.find(e => e.employeeId === employeeId)!,
    );
    const updated = existing.filter(c => c.employeeId !== employeeId);
    updated.push({ employeeId, enabled, rate: current?.rate ?? etbRate });
    setFieldValue('employeeCommissions', updated);
  };

  const setRate = (employeeId: string, rate: string) => {
    const existing = values.employeeCommissions ?? [];
    const current = existing.find(c => c.employeeId === employeeId);
    const updated = existing.filter(c => c.employeeId !== employeeId);
    updated.push({
      employeeId,
      enabled: current?.enabled ?? false,
      rate: Number(rate),
    });
    setFieldValue('employeeCommissions', updated);
  };

  return (
    <Stack gap={1.5}>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <Typography variant="h5">Service Commission Rates</Typography>
        <Tooltip
          arrow
          enterTouchDelay={0}
          title={`Toggle on to set a custom commission rate for this provider on this specific service. If toggled off, the provider's default payroll settings apply. ${NON_COMMISSION_TOOLTIP}`}>
          <InfoOutlinedIcon sx={{ fontSize: 14, cursor: 'pointer' }} />
        </Tooltip>
      </Stack>
      {selectedEmployees.map(employee => {
        const commission = getCommission(employee.employeeId);
        const isCommissionType = employee.payrollType === 'COMMISSION';
        const isEnabled = commission?.enabled ?? false;
        const etbRate = getEtbRate(employee);
        const displayRate = isEnabled
          ? String(commission?.rate ?? etbRate)
          : String(etbRate);

        return (
          <Stack
            key={employee.employeeId}
            direction="row"
            alignItems="center"
            gap={1.5}>
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              flexShrink={1}
              minWidth={0}
              flex={1}>
              <Tooltip
                arrow
                enterTouchDelay={0}
                title={!isCommissionType ? NON_COMMISSION_TOOLTIP : ''}>
                <span>
                  <IOSSwitch
                    id={`commission-toggle-${employee.employeeId}`}
                    checked={isEnabled}
                    onChange={() => setEnabled(employee.employeeId, !isEnabled)}
                    disabled={!isCommissionType || isSubmitting}
                  />
                </span>
              </Tooltip>
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                {employee.displayName || 'Unnamed provider'}
              </Typography>
            </Stack>
            <Stack flexShrink={0} width={110}>
              <TextInputControlled
                id={`commission-rate-${employee.employeeId}`}
                type="number"
                suffix="%"
                disabled={!isEnabled || !isCommissionType || isSubmitting}
                value={displayRate}
                min={0}
                max={100}
                step={0.1}
                onChange={val => setRate(employee.employeeId, val)}
              />
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}

export default ServiceCommissionsSection;
